import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Pager } from '../../components/Pager';
import { SectorIntelFilterBar } from './SectorIntelFilterBar';
import { SectorGroupsTable } from './SectorGroupsTable';
import { useSectorIntelFilterOptions } from './useSectorIntel';
import { useSectorGroupsList } from './useSectorGroups';
import {
  DEFAULT_SECTOR_INTEL_FILTERS,
  type SectorGroupItem,
  type SectorIntelFilters,
} from './sectorIntel.types';
import { downloadSectorGroupsExport } from '../../api/sectorIntelApi';
import '../PendingCAEvents/pendingCa.css';
import './sectorIntel.css';

interface ToastItem {
  id: string;
  msg: string;
  tone: 'info' | 'success' | 'error' | 'warn';
}

// 쿼리스트링 초기 반영: canonical_sector_id 만 받는다. (sector_name 등은 종목 화면 전용.)
function buildInitialFilters(params: URLSearchParams): SectorIntelFilters {
  const canonicalSectorId = params.get('canonical_sector_id')?.trim() ?? '';
  if (!canonicalSectorId) return DEFAULT_SECTOR_INTEL_FILTERS;
  return { ...DEFAULT_SECTOR_INTEL_FILTERS, canonicalSectorId };
}

export default function SectorGroupsPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // 마운트 시 1회 — 이후 필터는 로컬 state 권위.
  const [filters, setFilters] = useState<SectorIntelFilters>(() =>
    buildInitialFilters(searchParams)
  );
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [exporting, setExporting] = useState(false);

  const pushToast = useCallback(
    (msg: string, tone: ToastItem['tone'] = 'info') => {
      const id = Math.random().toString(36).slice(2);
      setToasts((prev) => [...prev, { id, msg, tone }]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 4500);
    },
    []
  );

  const list = useSectorGroupsList(filters);
  const options = useSectorIntelFilterOptions();

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;

  const pageInfo = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(total / Math.max(1, filters.size)));
    return { totalPages, current: filters.page + 1 };
  }, [total, filters.size, filters.page]);

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await downloadSectorGroupsExport(filters);
      pushToast('엑셀 다운로드 완료', 'success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'export_failed';
      const [code, detail] = msg.split(':');
      if (code === 'export_too_large') {
        pushToast('결과가 너무 큽니다. 필터를 더 좁혀주세요.', 'error');
      } else {
        pushToast(detail ?? msg, 'error');
      }
    } finally {
      setExporting(false);
    }
  }, [exporting, filters, pushToast]);

  const jumpToPage = useCallback(
    (nextPage: number) => {
      const clamped = Math.max(0, Math.min(pageInfo.totalPages - 1, nextPage));
      if (clamped !== filters.page) {
        setFilters({ ...filters, page: clamped });
      }
    },
    [filters, pageInfo.totalPages]
  );

  const handleRowClick = useCallback(
    (item: SectorGroupItem) => {
      const qs = new URLSearchParams();
      qs.set('sector_name', item.sectorName);
      qs.set('sector_type', item.sectorType);
      navigate(`/sector-intelligence/stocks?${qs.toString()}`);
    },
    [navigate]
  );

  return (
    <div className="pca-page">
      <SectorIntelFilterBar
        filters={filters}
        availableSources={options.data?.sources ?? []}
        onChange={setFilters}
        onExport={handleExport}
        exporting={exporting}
      />

      {list.isError && (
        <div className="pca-warning-banner">
          목록 로드 실패: {(list.error as Error).message}
        </div>
      )}

      <SectorGroupsTable items={items} isLoading={list.isLoading} onRowClick={handleRowClick} />

      <Pager
        page={filters.page}
        pageSize={filters.size}
        total={total}
        onPageChange={(p) => jumpToPage(p)}
        pageSizeOptions={[50, 100, 200, 500]}
        onPageSizeChange={(size) => setFilters({ ...filters, size, page: 0 })}
      />


      <div className="pca-toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`pca-toast pca-toast--${t.tone}`}>
            {t.msg}
          </div>
        ))}
      </div>
    </div>
  );
}

